import { createBrowserLocalDataDriver } from './browserStorageDriver';
import type { LocalDataDriver, LocalDataMetadata, LocalDataRecord, LocalDataScope } from './types';

const DEFAULT_DRIVER = createBrowserLocalDataDriver();
export const LOCAL_DATA_SCHEMA_VERSION = 2;

/**
 * Gate 478: the cache is a cache. A store that will not open in time (`INDEXED_DB_OPEN_DEADLINE_MS`,
 * a busy machine), is held by another window, or fails a request costs this screen its head start
 * and nothing else: a read finds nothing and the screen reads the network, a write or a removal is
 * skipped. It never rejects to its caller — several write and forget (`void localSyncStore.write`),
 * and each such rejection was an uncaught page error. One handled warning per kind of failure.
 */
const warnedCacheFailures = new Set<string>();

function cacheUnavailable(operation: string, error: unknown): void {
  const reason = error instanceof Error ? error.message : String(error);
  if (warnedCacheFailures.has(reason)) return;
  warnedCacheFailures.add(reason);
  console.warn(`[featit] local cache unavailable (${operation}); using the network:`, reason);
}

/**
 * T-0655 (RD-39): how long any one call on the store may take before it is given up — a read finds
 * nothing, a write or a removal is skipped. The open has its own deadline
 * (`INDEXED_DB_OPEN_DEADLINE_MS`); this one covers an open store that does not answer: a request
 * queues behind any other connection's transaction on it, and the iPhone app keeps several web views
 * open on one store, so without it a reader waited for as long as another view held the store.
 * Every reader inherits it here, rather than each keeping its own.
 */
export const LOCAL_STORE_DEADLINE_MS = 3_000;

/** A call on the local store that did not answer within {@link LOCAL_STORE_DEADLINE_MS}. */
export class LocalStoreDeadlineError extends Error {
  constructor(readonly timeoutMs: number) {
    super(`The local store did not answer within ${timeoutMs} ms.`);
    this.name = 'LocalStoreDeadlineError';
  }
}

/**
 * I-1895: collections an earlier version cached and no code reads or writes now. Their records are
 * removed from every store at its first use, so a work area this device never opens again does not
 * keep them. `work-area-dashboard-pages` held each work area's page list, titles of pages the reader
 * may have lost since.
 *
 * Retired records are purged on every store at start (I-1895): `purgeRetiredStores.ts` enumerates the
 * stores on the device and purges each one once, off the first-render path, so a work area this device
 * never opens again keeps nothing; a store used earlier than that purges itself at its first use.
 */
export const RETIRED_COLLECTIONS: readonly string[] = ['work-area-dashboard-pages'];

export class LocalSyncStore {
  private retiredPurge: Promise<void> | undefined;

  constructor(
    private readonly driver: LocalDataDriver = DEFAULT_DRIVER,
    private readonly deadlineMs: number = LOCAL_STORE_DEADLINE_MS,
  ) {}

  /**
   * Removes the retired collections' records from this store now, once, and resolves when that is
   * done or skipped. Never rejects. Used by the start-up purge of every store on the device
   * (`purgeRetiredStores.ts`), which does not wait for a store to be used.
   */
  async purgeRetiredNow(): Promise<void> {
    this.purgeRetired();
    await this.retiredPurge;
  }

  /**
   * Removes the retired collections' records once per store, started by the store's first call and
   * not waited for: a store that does not answer costs its callers no second deadline. A failure
   * is a skipped purge.
   */
  private purgeRetired(): void {
    this.retiredPurge ??= this.withinDeadline(async () => {
      for (const collection of RETIRED_COLLECTIONS) {
        for (const record of await this.driver.list(collection)) {
          await this.driver.delete(record.collection, record.key);
        }
      }
    }).catch((error: unknown) => cacheUnavailable('purge', error));
  }

  async read<T>(collection: string, key: string, scope?: LocalDataScope): Promise<T | null> {
    this.purgeRetired();
    try {
      return await this.withinDeadline(async () => {
        const record = await this.driver.get<T>(collection, key);
        if (!record) return null;
        const migrated = migrateLocalDataRecord(record, scope);
        if (!migrated) {
          await this.driver.delete(collection, key);
          return null;
        }
        if (migrated !== record) await this.driver.put(migrated);
        return migrated.value;
      });
    } catch (error) {
      cacheUnavailable('read', error);
      return null;
    }
  }

  async write<T>(
    collection: string,
    key: string,
    value: T,
    metadata: LocalDataMetadata<T> = {},
  ): Promise<void> {
    const workAreaId = resolveMetadataWorkAreaId(metadata);
    this.purgeRetired();
    await this.withinDeadline(() =>
      this.driver.put({
        schemaVersion: LOCAL_DATA_SCHEMA_VERSION,
        collection,
        key,
        value,
        updatedAt: new Date().toISOString(),
        dirty: metadata.dirty,
        serverLineage: metadata.serverLineage,
        revision: metadata.revision,
        digest: metadata.digest,
        ...(workAreaId ? { workAreaId } : {}),
      }),
    ).catch((error: unknown) => cacheUnavailable('write', error));
  }

  async remove(collection: string, key: string): Promise<void> {
    this.purgeRetired();
    await this.withinDeadline(() => this.driver.delete(collection, key)).catch((error: unknown) =>
      cacheUnavailable('remove', error),
    );
  }

  async list<T>(collection: string, scope?: LocalDataScope): Promise<Array<LocalDataRecord<T>>> {
    let records: Array<LocalDataRecord<T>>;
    this.purgeRetired();
    try {
      records = await this.withinDeadline(() => this.driver.list<T>(collection));
    } catch (error) {
      cacheUnavailable('list', error);
      return [];
    }
    const migrated: Array<LocalDataRecord<T>> = [];
    const repairs: Array<Promise<void>> = [];
    for (const record of records) {
      const next = migrateLocalDataRecord(record, scope);
      if (!next) {
        repairs.push(this.driver.delete(record.collection, record.key));
        continue;
      }
      migrated.push(next);
      if (next !== record) repairs.push(this.driver.put(next));
    }
    await this.withinDeadline(() => Promise.all(repairs)).catch((error: unknown) =>
      cacheUnavailable('repair', error),
    );
    return migrated;
  }

  /**
   * `work`, or a {@link LocalStoreDeadlineError} once the deadline passes. The driver is called
   * before the timer is set, so a deadline of its own at the same length (the open's) fires first
   * and names the cause. Work given up on still finishes in the background; nothing waits for it.
   */
  private withinDeadline<T>(work: () => Promise<T>): Promise<T> {
    const pending = work();
    let timer: ReturnType<typeof setTimeout> | undefined;
    const deadline = new Promise<never>((_, reject) => {
      timer = setTimeout(
        () => reject(new LocalStoreDeadlineError(this.deadlineMs)),
        this.deadlineMs,
      );
    });
    return Promise.race([pending, deadline]).finally(() => clearTimeout(timer));
  }
}

function migrateLocalDataRecord<T>(
  record: LocalDataRecord<T>,
  scope?: LocalDataScope,
): LocalDataRecord<T> | null {
  const canonicalId = record.workAreaId?.trim() || null;
  const legacyId = record.projectId?.trim() || null;
  if (canonicalId && legacyId && canonicalId !== legacyId) return null;
  const persistedId = canonicalId ?? legacyId;
  if (scope && persistedId && persistedId !== scope.workAreaId) return null;
  const workAreaId = scope?.workAreaId ?? persistedId;
  if (
    record.schemaVersion === LOCAL_DATA_SCHEMA_VERSION &&
    record.projectId === undefined &&
    record.workAreaId === workAreaId
  ) {
    return record;
  }
  const { projectId: _legacyProjectId, ...canonicalRecord } = record;
  return {
    ...canonicalRecord,
    schemaVersion: LOCAL_DATA_SCHEMA_VERSION,
    ...(workAreaId ? { workAreaId } : {}),
  };
}

function resolveMetadataWorkAreaId(metadata: LocalDataMetadata): string | null {
  const canonicalId = metadata.workAreaId?.trim() || null;
  const legacyId = metadata.projectId?.trim() || null;
  if (canonicalId && legacyId && canonicalId !== legacyId) {
    throw new Error('workAreaId and deprecated projectId must identify the same work area');
  }
  return canonicalId ?? legacyId;
}

/** Tests only: warn again for a kind of failure already warned about. */
export function resetCacheWarningsForTests(): void {
  warnedCacheFailures.clear();
}

export const localSyncStore = new LocalSyncStore();
